// @vendors
import React, { useContext, useMemo, useState } from "react";
import { useSelector } from "react-redux";

// @state
import { useUI } from "../../../app/context/ui";

// @assets
import { EmployeeTableStyles } from "../../../assets/css/employeeTable-style";
import { GeneralStyles } from "../../../assets/css/general-style";

// @components
import { SearchEmployeeContext } from "../SearchEmployees";
import {
  Box,
  Button,
  ClearAllIcon,
  FilterListRoundedIcon,
  IconButton,
  InputAdornment,
  SearchRoundedIcon,
  TextField,
  Tooltip,
  Link,
  Grid,
} from "../../../components/shared/MaterialUI";
import FilterStatus from "../../Employer/components/EmployeeTable/components/FilterStatus";

// @helperts
import AppHelper from "../../../helpers/AppHelper";
import { permissionsForAddOrEditEmployees } from "../../../helpers/FunctionsUtils";

// @services
import EmployeeCompanyServiceNewApi from "../../../services/newApi/EmployeeCompanyServiceNewApi";
import { theme } from "../../../assets/theme";

const TableToolbarSearch = () => {
  const {
    tableState,
    setTableState,
    fetchAllEmployees
  } = useContext(SearchEmployeeContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const styles = useMemo(() => EmployeeTableStyles(), []);
  const generalStyle = useMemo(() => GeneralStyles(), []);
  const id = open ? "simple-popover" : undefined;
  const { blockUI, snackbarUI } = useUI();
  const employeeServiceNewApi = new EmployeeCompanyServiceNewApi();
  const globalState = useSelector((loadedState) => loadedState);
  const user = globalState.user;
  const organization = globalState.organizationReducer?.organizationSelected;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const downloadEmployees = async () => {
    try {
      blockUI.current.open(true);
      const res = await employeeServiceNewApi.exportEmployeesByCompany('', organization?.id);
      fileToConvert(res?.data);
      blockUI.current.open(false);
    } catch (e) {
      blockUI.current.open(false);
      AppHelper.checkError(e, snackbarUI);
    }
  };

  const fileToConvert = (res) => {
    //const blob = new Blob([res?.data], { type: "text/csv" });
    const blob = new Blob([res], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "employees.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const onSelectChange = (status) => {
    setTableState((current) => ({
      ...current,
      filters: {...current.filters, status: status},
    }));
    const filters = { name: tableState.filters.name, status: status };
    fetchAllEmployees({filters:filters});
    setAnchorEl(null);
  };

  const handleSearchBar = async (query) => {
    if (query) fetchAllEmployees();
  };

  const onSearchBarChange = (e) => {
    setTableState((current) => ({
      ...current,
      filters: {...current.filters, name: e.target.value},
    }));
  };

  const clearSearch = () => {
    setTableState((current) => ({
      ...current,
      pageSize: 10,
      rows: [],
      rowCount: 0,
      currentPage: 0,
      page: 0,
      filters: { name: '', status: '' },
    }));
    setAnchorEl(null);
    //fetchAllEmployees({filters:{}});
  };

  const actionToSelect = () => {
    if(tableState.filters.name === '')return clearSearch();
    if(tableState.filters.name?.length > 0)return handleSearchBar(tableState.filters.name);
    return
  }

  return (
    <Box sx={styles.tableToolbar}>
      <Grid container direction="row" alignItems="center" spacing={3}>
        <Grid item xs={12} sm={12} md={3} lg={2}>
          <h2 style={{ color: theme.palette.primary.black }}>Employee list</h2>
        </Grid>

        <Grid item xs={12} sm={8} md={6} lg={5} sx={{display: 'flex', gap: '10px', alignItems: 'center'}}>
          <TextField
            sx={{...styles.searchInput, ...generalStyle.customButtonSm}}
            value={tableState.filters.name}
            id="searchByEmployee"
            placeholder="Search"
            onChange={(e) => onSearchBarChange(e)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if(tableState.filters.name === '') return clearSearch()
                return handleSearchBar(tableState.filters.name);
              }
            }}
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <IconButton onClick={actionToSelect} sx={generalStyle.iconButtonSearch}>
                    <SearchRoundedIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Clear filter">
            <IconButton
              onClick={() => clearSearch()}
              sx={generalStyle.iconButtonSearch}
            >
              <ClearAllIcon />
            </IconButton>
          </Tooltip>
        </Grid>

        <Grid item xs={12} sm={4} md={3} lg={2}>
          <Tooltip title="Show filters">
            <Button
              aria-describedby={id}
              variant="contained"
              onClick={handleClick}
              sx={{...generalStyle.customButton, ...generalStyle.customButtonSm}}
            >
              Filter
              <FilterListRoundedIcon />
            </Button>
          </Tooltip>
        </Grid>

        {permissionsForAddOrEditEmployees(user?.role) &&
          <Grid item xs={12} sm={4} md={4} lg={2}>
            <Tooltip title="Export">
              <Link download onClick={downloadEmployees} sx={styles.exportLink}>
                <Button
                  variant="contained"
                  component="span"
                  sx={{...generalStyle.customButton, ...generalStyle.customButtonSm}}
                >
                  Export
                </Button>
              </Link>
            </Tooltip>
          </Grid>
        }

        <FilterStatus
          id={id}
          open={open}
          anchorEl={anchorEl}
          handleClose={handleClose}
          onSelectChange={onSelectChange}
          clearSearch={clearSearch}
        />
      </Grid>
    </Box>
  );
};

export default TableToolbarSearch;
